import { isMobile } from 'src/scripts/utils/check-media'
import { SidebarComponent } from '../../components/sidebar/sidebar'
import { getFocusTargets, $el, $list, $elParent } from '../../utils/utils'
import { DrawerMenuPage } from './drawer-menu-page'
import { HeaderComponent } from './header'

const HEADER_COMPONENT_SELECTOR = 'header-component'
const DRAWER_MENU_PAGE_SELECTOR = 'drawer-menu-page'
const TOGGLER_SELECTOR = '#DrawerMenuToggler'

export class DrawerMenu extends SidebarComponent {
  openedPage: DrawerMenuPage
  pageMap: { [key: string]: DrawerMenuPage }
  isCurrentModeMobile: boolean
  resizeObserver: ResizeObserver

  constructor() {
    super()
    this.pageMap = this.generatePageMap()
    this.isCurrentModeMobile = isMobile()
  }

  mountComponent(): void {
    super.mountComponent()

    const header = $el(HEADER_COMPONENT_SELECTOR) as HeaderComponent

    header.on('drawerActivated', this.handleSetAsSection)
    header.on('drawerUnActivated', this.handleUnsetAsSection)

    if (this.isEditor) {
      this.editor.on('BLOCK_SELECT', this.handleBlockSelect)
      this.editor.on('BLOCK_DESELECT', this.handleBlockDeselect)
      this.editor.on('SECTION_SELECT', this.handleSectionSelect)
      this.editor.on('SECTION_DESELECT', this.handleSectionDeselect)
    }
  }

  unmountComponent(): void {
    super.unmountComponent()

    const header = $el(HEADER_COMPONENT_SELECTOR) as HeaderComponent

    if (this.isEditor) {
      this.editor.off('BLOCK_SELECT', this.handleBlockSelect)
      this.editor.off('BLOCK_DESELECT', this.handleBlockDeselect)
      this.editor.off('SECTION_SELECT', this.handleSectionSelect)
      this.editor.off('SECTION_DESELECT', this.handleSectionDeselect)

      header.off('headerNavFitted', this.handleSetAsSection)
    }

    header.off('headerNavUnfitted', this.handleUnsetAsSection)
  }

  handleSetAsSection = () => {
    this.setAttribute('data-drawer-menu-is-section', '')
  }

  handleUnsetAsSection = () => {
    this.removeAttribute('data-drawer-menu-is-section')

    if (this.isOpen) {
      this.hide()
    }
  }

  handleResize = (): void => {
    if (this.isCurrentModeMobile !== isMobile() && this.isOpen) {
      this.hide(true)
    }

    this.isCurrentModeMobile = isMobile()
  }

  handleSectionSelect = ({ detail: { sectionId } }): void => {
    if (this.isVisible && this.dataset.sectionId === sectionId) {
      this.open()
      this.reset()
    }
  }

  handleSectionDeselect = ({ detail: { sectionId } }): void => {
    if (this.isVisible && this.dataset.sectionId === sectionId) {
      this.reset()
      this.hide(true)
    }
  }

  handleBlockSelect = ({ detail: { blockId, sectionId } }): void => {
    if (this.isVisible && this.dataset.sectionId === sectionId) {
      const pages: DrawerMenuPage[] = $list(
        DRAWER_MENU_PAGE_SELECTOR,
        this.element,
      )

      const target = pages.find(page =>
        page.querySelector(`[block-id='${blockId}']`),
      )

      if (!target) {
        return
      }

      this.open()
      this.setPage(target)
    }
  }

  handleBlockDeselect = ({ detail: { blockId, sectionId } }): void => {
    if (this.isVisible && this.dataset.sectionId === sectionId) {
      const pages: DrawerMenuPage[] = $list(
        DRAWER_MENU_PAGE_SELECTOR,
        this.element,
      )
      const target = pages.find(page =>
        page.querySelector(`[block-id='${blockId}']`),
      )

      if (!target) {
        return
      }

      this.hide(true)
    }
  }

  handlePageKeydown = (event: KeyboardEvent) => {
    event.preventDefault()

    const target = event.target as HTMLElement
    const page = $elParent(DRAWER_MENU_PAGE_SELECTOR, target)

    if (!page) {
      return
    }

    const targets = $list(getFocusTargets(), page)
    const isLast = target === targets[targets.length - 1]
    const isFirst = target === targets[0]
    const isShift = event.shiftKey

    if ((isShift && isFirst) || (!isShift && isLast)) {
      this.focusOnToggler()
    }
  }

  mount() {
    super.mount()

    this.addListener(this, 'keydown', this.handlePageKeydown)

    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this)
  }

  unmount(): void {
    this.removeListener(this, 'keydown', this.handlePageKeydown)

    this.resizeObserver?.disconnect()

    super.unmount()
  }

  focusOnToggler() {
    this.hide().then(() => {
      const toggler = $el(TOGGLER_SELECTOR)
      toggler?.focus()
    })
  }

  generatePageMap() {
    const pages: DrawerMenuPage[] = $list(
      DRAWER_MENU_PAGE_SELECTOR,
      this.element,
    )
    const pageMap = pages.reduce((map, page) => {
      const pageId = page.dataset.pageId
      return pageId ? { ...map, [pageId]: page } : map
    }, {})

    return pageMap
  }

  setPage(page: DrawerMenuPage): void {
    const pageId = page.dataset.pageId
    const prevOpenedPageId = this.openedPage?.dataset.prevPageId
    const isReturn = pageId === prevOpenedPageId

    if (this.openedPage) {
      if (isReturn) {
        this.setPageVisible(this.openedPage, false)
      }
    }

    const firstFocusTarget = $el(getFocusTargets(), page)

    if (firstFocusTarget) {
      firstFocusTarget.focus()
    }

    this.setPageVisible(page, true)
    this.openedPage = page
  }

  reset(): void {
    const pages: DrawerMenuPage[] = $list(
      DRAWER_MENU_PAGE_SELECTOR,
      this.element,
    )
    const homePage = this.pageMap['Menu']
    pages.forEach(page => {
      this.setPageVisible(page, false)
    })
    this.setPageVisible(homePage, true)
    this.openedPage = homePage
  }

  setPageVisible(page: DrawerMenuPage, isVisible: boolean): void {
    page.setAttribute('aria-hidden', isVisible ? 'false' : 'true')
  }

  async open(): Promise<void> {
    const header: HeaderComponent = $el(HEADER_COMPONENT_SELECTOR)
    header?.updatePosition()

    super.open()
  }

  async hide(isInstant?: boolean): Promise<void> {
    this.reset()
    super.hide(isInstant)
  }

  get isVisible(): boolean {
    const isLowResolution = matchMedia('(max-width: 992px)').matches
    const isSection = this.hasAttribute('data-drawer-menu-is-section')

    return isSection || isLowResolution
  }
}
